import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import { indexTableQueryOptions } from '@/lib/services/index-table-query-options'
import queryClient from '@/lib/queryClient'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { queryOptions, useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query'
import {
  cancelFlowerSubscription,
  getFlowerSubscription,
  getFlowerSubscriptions,
  notifyFlowerSubscriptionFulfillmentUnavailable,
  resumeSkippedFlowerSubscription,
  skipFlowerSubscription,
  updateFlowerSubscriptionFulfillmentDate,
  previewFlowerSubscriptionFulfillment,
  updateFlowerSubscriptionFulfillment,
  updateFlowerSubscriptionPaymentMethod,
  updateFlowerSubscriptionPersonalization,
  updateFlowerSubscriptionRecipient,
  updateFlowerSubscriptionSchedule,
  type FlowerSubscriptionIndexKeyEntries,
  type FlowerSubscriptionKeyEntries,
  type FlowerSubscriptionShowKeyEntries,
} from './flower-subscriptions.api'
import type {
  CancelFlowerSubscriptionInput,
  FlowerSubscriptionDestinationInput,
  FlowerSubscriptionIdInput,
  FlowerSubscriptionIndexFilters,
  NotifyFlowerSubscriptionFulfillmentUnavailableInput,
  SkipFlowerSubscriptionInput,
  UpdateFlowerSubscriptionFulfillmentDateInput,
  UpdateFlowerSubscriptionFulfillmentInput,
  UpdateFlowerSubscriptionPaymentMethodInput,
  UpdateFlowerSubscriptionPersonalizationInput,
  UpdateFlowerSubscriptionRecipientInput,
  UpdateFlowerSubscriptionScheduleInput,
} from './flower-subscriptions.types'

/** Without an organization, the root key matches every organization; hooks fall back to the route's. */
interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

/** Scope for reading one subscription; the Laravel route requires its id. */
interface FlowerSubscriptionShowScope extends FlowerSubscriptionKeyEntries {
  flowerSubscriptionId: FlowerSubscriptionIdInput
}

/** Mutation variables name the subscription; the hooks add the route's organization. */
interface FlowerSubscriptionRef {
  flowerSubscriptionId: FlowerSubscriptionIdInput
}

/** The key entries each fetcher reads; the scoped key builders below set them. */
type FlowerSubscriptionIndexQueryKey = [AdminQueryRoot & FlowerSubscriptionIndexKeyEntries]
type FlowerSubscriptionShowQueryKey = [AdminQueryRoot & FlowerSubscriptionShowKeyEntries]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

export const flowerSubscriptionQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'flowerSubscription' }, { organizationId }),
  ],
  index: ({
    organizationId,
    filters,
  }: FlowerSubscriptionKeyEntries & {
    filters?: FlowerSubscriptionIndexFilters | undefined
  }): FlowerSubscriptionIndexQueryKey =>
    buildScopedAdminQueryKey(flowerSubscriptionQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'index',
      filters,
    }),
  pagedIndex: ({
    organizationId,
    filters,
    page,
    pageSize,
  }: FlowerSubscriptionIndexKeyEntries): FlowerSubscriptionIndexQueryKey =>
    buildScopedAdminQueryKey(flowerSubscriptionQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'index',
      filters,
      page,
      pageSize,
    }),
  show: ({
    organizationId,
    flowerSubscriptionId,
  }: FlowerSubscriptionShowScope): FlowerSubscriptionShowQueryKey =>
    buildScopedAdminQueryKey(flowerSubscriptionQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'show',
      flowerSubscriptionId: Number(flowerSubscriptionId),
    }),
}

export const flowerSubscriptionQueries = {
  index: ({ organizationId, filters, page, pageSize }: FlowerSubscriptionIndexKeyEntries) =>
    indexTableQueryOptions({
      queryKey: flowerSubscriptionQueryKeys.pagedIndex({
        organizationId,
        filters,
        page,
        pageSize,
      }),
      queryFn: getFlowerSubscriptions,
    }),

  show: ({ organizationId, flowerSubscriptionId }: FlowerSubscriptionShowScope) =>
    queryOptions({
      queryKey: flowerSubscriptionQueryKeys.show({
        organizationId,
        flowerSubscriptionId,
      }),
      queryFn: getFlowerSubscription,
    }),
}

export function useFlowerSubscriptions({
  organizationId,
  filters,
  page,
  pageSize,
  ...params
}: OrganizationScope &
  Omit<FlowerSubscriptionIndexKeyEntries, 'organizationId'> &
  QueryParams<ReturnType<typeof flowerSubscriptionQueries.index>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...flowerSubscriptionQueries.index({
      organizationId: resolvedOrganizationId,
      filters,
      page,
      pageSize,
    }),
    ...params,
  })
}

export function useFlowerSubscription(
  flowerSubscriptionId: FlowerSubscriptionIdInput,
  {
    organizationId,
    ...params
  }: OrganizationScope & QueryParams<ReturnType<typeof flowerSubscriptionQueries.show>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...flowerSubscriptionQueries.show({
      organizationId: resolvedOrganizationId,
      flowerSubscriptionId,
    }),
    ...params,
  })
}

/** Skip and resume also take the subscription id alone. */
function toFlowerSubscriptionRef<TVariables extends FlowerSubscriptionRef>(
  variables: FlowerSubscriptionIdInput | TVariables
): TVariables | FlowerSubscriptionRef {
  return typeof variables === 'object' && variables !== null
    ? variables
    : { flowerSubscriptionId: variables }
}

export function useSkipFlowerSubscriptionMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (
      variables: FlowerSubscriptionIdInput | (FlowerSubscriptionRef & SkipFlowerSubscriptionInput)
    ) => skipFlowerSubscription({ organizationId, ...toFlowerSubscriptionRef(variables) }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: flowerSubscriptionQueryKeys.all({ organizationId }),
      }),
  })
}

export function useResumeSkippedFlowerSubscriptionMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (variables: FlowerSubscriptionIdInput | FlowerSubscriptionRef) =>
      resumeSkippedFlowerSubscription({ organizationId, ...toFlowerSubscriptionRef(variables) }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: flowerSubscriptionQueryKeys.all({ organizationId }),
      }),
  })
}

export function useCancelFlowerSubscriptionMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: ({
      flowerSubscriptionId,
      notifyCustomer = true,
    }: FlowerSubscriptionRef & CancelFlowerSubscriptionInput) =>
      cancelFlowerSubscription({
        organizationId,
        flowerSubscriptionId,
        notifyCustomer,
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: flowerSubscriptionQueryKeys.all({ organizationId }),
      }),
  })
}

export function useUpdateFlowerSubscriptionPaymentMethodMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: ({
      flowerSubscriptionId,
      paymentMethodId,
    }: FlowerSubscriptionRef & UpdateFlowerSubscriptionPaymentMethodInput) =>
      updateFlowerSubscriptionPaymentMethod({
        organizationId,
        flowerSubscriptionId,
        paymentMethodId,
      }),
    onSuccess: (response, variables) => {
      if (response?.data) {
        queryClient.setQueryData(
          flowerSubscriptionQueryKeys.show({
            organizationId,
            flowerSubscriptionId: variables.flowerSubscriptionId,
          }),
          response.data
        )
      }

      return queryClient.invalidateQueries({
        queryKey: flowerSubscriptionQueryKeys.all({ organizationId }),
      })
    },
  })
}

export function useUpdateFlowerSubscriptionFulfillmentDateMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: ({
      flowerSubscriptionId,
      fulfillmentDate,
      allowUnavailable,
    }: FlowerSubscriptionRef & UpdateFlowerSubscriptionFulfillmentDateInput) =>
      updateFlowerSubscriptionFulfillmentDate({
        organizationId,
        flowerSubscriptionId,
        fulfillmentDate,
        allowUnavailable,
      }),
    onSuccess: (response, variables) => {
      if (response?.data) {
        queryClient.setQueryData(
          flowerSubscriptionQueryKeys.show({
            organizationId,
            flowerSubscriptionId: variables.flowerSubscriptionId,
          }),
          response.data
        )
      }

      return queryClient.invalidateQueries({
        queryKey: flowerSubscriptionQueryKeys.all({ organizationId }),
      })
    },
  })
}

export function useUpdateFlowerSubscriptionScheduleMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: ({
      flowerSubscriptionId,
      frequency,
      preferredFulfillmentWeekday,
      nextFulfillmentDate,
      expectedFrequency,
      expectedPreferredFulfillmentWeekday,
      expectedNextFulfillmentDate,
      allowUnavailable,
      immediateChargeConfirmed,
    }: FlowerSubscriptionRef & UpdateFlowerSubscriptionScheduleInput) =>
      updateFlowerSubscriptionSchedule({
        organizationId,
        flowerSubscriptionId,
        frequency,
        preferredFulfillmentWeekday,
        nextFulfillmentDate,
        expectedFrequency,
        expectedPreferredFulfillmentWeekday,
        expectedNextFulfillmentDate,
        allowUnavailable,
        immediateChargeConfirmed,
      }),
    onSuccess: (response, variables) => {
      if (response?.data) {
        queryClient.setQueryData(
          flowerSubscriptionQueryKeys.show({
            organizationId,
            flowerSubscriptionId: variables.flowerSubscriptionId,
          }),
          response.data
        )
      }

      return queryClient.invalidateQueries({
        queryKey: flowerSubscriptionQueryKeys.all({ organizationId }),
      })
    },
    onError: () =>
      queryClient.invalidateQueries({
        queryKey: flowerSubscriptionQueryKeys.all({ organizationId }),
      }),
  })
}

export function usePreviewFlowerSubscriptionFulfillmentMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (variables: FlowerSubscriptionRef & FlowerSubscriptionDestinationInput) =>
      previewFlowerSubscriptionFulfillment({ organizationId, ...variables }),
  })
}

export function useUpdateFlowerSubscriptionFulfillmentMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (variables: FlowerSubscriptionRef & UpdateFlowerSubscriptionFulfillmentInput) =>
      updateFlowerSubscriptionFulfillment({ organizationId, ...variables }),
    onSuccess: (response, variables) => {
      if (response?.data) {
        queryClient.setQueryData(
          flowerSubscriptionQueryKeys.show({
            organizationId,
            flowerSubscriptionId: variables.flowerSubscriptionId,
          }),
          response.data
        )
      }

      return queryClient.invalidateQueries({
        queryKey: flowerSubscriptionQueryKeys.all({ organizationId }),
      })
    },
    onError: () =>
      queryClient.invalidateQueries({
        queryKey: flowerSubscriptionQueryKeys.all({ organizationId }),
      }),
  })
}

export function useNotifyFlowerSubscriptionFulfillmentUnavailableMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: ({
      flowerSubscriptionId,
      message,
    }: FlowerSubscriptionRef & NotifyFlowerSubscriptionFulfillmentUnavailableInput) =>
      notifyFlowerSubscriptionFulfillmentUnavailable({
        organizationId,
        flowerSubscriptionId,
        message,
      }),
    onSuccess: (response, variables) => {
      if (response?.data) {
        queryClient.setQueryData(
          flowerSubscriptionQueryKeys.show({
            organizationId,
            flowerSubscriptionId: variables.flowerSubscriptionId,
          }),
          response.data
        )
      }

      return queryClient.invalidateQueries({
        queryKey: flowerSubscriptionQueryKeys.all({ organizationId }),
      })
    },
  })
}

export function useUpdateFlowerSubscriptionPersonalizationMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: ({
      flowerSubscriptionId,
      cardMessage,
      signature,
      specialInstructions,
    }: FlowerSubscriptionRef & UpdateFlowerSubscriptionPersonalizationInput) =>
      updateFlowerSubscriptionPersonalization({
        organizationId,
        flowerSubscriptionId,
        cardMessage,
        signature,
        specialInstructions,
      }),
    onSuccess: (response, variables) => {
      if (response?.data) {
        queryClient.setQueryData(
          flowerSubscriptionQueryKeys.show({
            organizationId,
            flowerSubscriptionId: variables.flowerSubscriptionId,
          }),
          response.data
        )
      }

      return queryClient.invalidateQueries({
        queryKey: flowerSubscriptionQueryKeys.all({ organizationId }),
      })
    },
  })
}

export function useUpdateFlowerSubscriptionRecipientMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: ({
      flowerSubscriptionId,
      recipientName,
      recipientPhone,
      recipientEmail,
    }: FlowerSubscriptionRef & UpdateFlowerSubscriptionRecipientInput) =>
      updateFlowerSubscriptionRecipient({
        organizationId,
        flowerSubscriptionId,
        recipientName,
        recipientPhone,
        recipientEmail,
      }),
    onSuccess: (response, variables) => {
      if (response?.data) {
        queryClient.setQueryData(
          flowerSubscriptionQueryKeys.show({
            organizationId,
            flowerSubscriptionId: variables.flowerSubscriptionId,
          }),
          response.data
        )
      }

      return queryClient.invalidateQueries({
        queryKey: flowerSubscriptionQueryKeys.all({ organizationId }),
      })
    },
  })
}
